/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, map, Observable, shareReplay } from 'rxjs';
import { CitiesJson, CountriesJson, Nationality, Settings } from '../models/common.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { userRole } from '../core/constants/keys.constant';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  selectedLanguage: BehaviorSubject<string> = new BehaviorSubject<string>('en');

  settingsSubject: BehaviorSubject<Settings | null> =
    new BehaviorSubject<Settings | null>(null);

  // These are static assets shared across the whole app (Add Student, Import
  // Students, School forms, etc.) — cached with shareReplay(1) so every
  // consumer/every open of a dialog reuses the same in-flight/completed
  // request instead of re-downloading the same JSON file repeatedly.
  private countries$?: Observable<Array<CountriesJson>>;
  private allCities$?: Observable<Array<CitiesJson>>;
  private nationality$?: Observable<Array<Nationality>>;

  constructor(private readonly http: HttpClient) {}

  removeEmptyKeys(obj: any): any {
    return Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      Object.entries(obj).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    );
  }

  getCountries(): Observable<Array<CountriesJson>> {
    if (!this.countries$) {
      this.countries$ = this.http
        .get<Array<CountriesJson>>('assets/countryLangJson/countries.json')
        .pipe(shareReplay(1));
    }
    return this.countries$;
  }

  /** Fetches (once, cached) the full country→cities dataset. Prefer this over getCities()
   * when cities for more than one country are needed, to avoid re-downloading the same file. */
  getAllCities(): Observable<Array<CitiesJson>> {
    if (!this.allCities$) {
      this.allCities$ = this.http
        .get<Array<CitiesJson>>('assets/countryLangJson/city.json')
        .pipe(shareReplay(1));
    }
    return this.allCities$;
  }

  getCities(country: string): Observable<any> {
    return this.getAllCities().pipe(map(data => data.find(item => item.country === country)));
  }

  getNationality(): Observable<Array<Nationality>> {
    if (!this.nationality$) {
      this.nationality$ = this.http
        .get<Array<Nationality>>('assets/countryLangJson/nationality.json')
        .pipe(shareReplay(1));
    }
    return this.nationality$;
  }

  getAllLanguages(): Observable<any> {
    return this.http
      .get<any>('assets/countryLangJson/allLanguages.json')
      .pipe(map(res => res));
  }

  getUserRoleDropDown(
    instructorId?: string,
    queryString?: string,
    forQuery?: string
  ): Observable<any> {
    interface DropdownResponse {
      data: Array<{ name: string; _id: string }>;
    }

    interface GroupDropDown {
      data: Array<{ groupName: string; _id: string }>;
    }

    // Base URLs without extra params
    const studentBaseUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.STUDENT}`;
    const parentBaseUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.PARENT}`;
    const tutorUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.TUTOR}${queryString ? `&${queryString}` : ''}`;
    const counselorUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.COUNSELOR}${queryString ? `&${queryString}` : ''}`;
    const adminUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.ADMIN}${queryString ? `&${queryString}` : ''}`;
    const groupUrl = `${environment.base_url}admin/tutorSession/getGroupDropdown`;

    // Conditionally append specific query params only for student and parent role URLs when instructorId is provided
    // const studentUrl = instructorId ? `${studentBaseUrl}&studentOf=${instructorId}` : (queryString ? `${studentBaseUrl}&${queryString}` : studentBaseUrl);
    const studentUrl = instructorId ? `${studentBaseUrl}&studentOf=${instructorId}` : studentBaseUrl;
    const parentUrl = instructorId ? `${parentBaseUrl}&associatedParent=${instructorId}` : (forQuery ? `${parentBaseUrl}&${forQuery}` : parentBaseUrl);

    return forkJoin({
      students: this.http.get<DropdownResponse>(studentUrl),
      tutors: this.http.get<DropdownResponse>(tutorUrl),
      counselors: this.http.get<DropdownResponse>(counselorUrl),
      group: this.http.get<GroupDropDown>(groupUrl),
      parents: this.http.get<DropdownResponse>(parentUrl),
      admin: this.http.get<DropdownResponse>(adminUrl)
    });
  }
  getUserRoleDropDownOne(
    queryString?: string
  ): Observable<any> {
    interface DropdownResponse {
      data: Array<{ name: string; _id: string }>;
    }

    interface GroupDropDown {
      data: Array<{ groupName: string; _id: string }>;
    }

    // Base URLs without extra params
    const studentBaseUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.STUDENT}${queryString ? `&${queryString}` : ''}`;
    const parentBaseUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.PARENT}${queryString ? `&${queryString}` : ''}`;
    const tutorUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.TUTOR}${queryString ? `&${queryString}` : ''}`;
    const counselorUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.COUNSELOR}${queryString ? `&${queryString}` : ''}`;
    const adminUrl = `${environment.base_url}admin/student/dropdown?role=${userRole.ADMIN}${queryString ? `&${queryString}` : ''}`;
    const groupUrl = `${environment.base_url}admin/tutorSession/getGroupDropdown`;
    return forkJoin({
      students: this.http.get<DropdownResponse>(studentBaseUrl),
      tutors: this.http.get<DropdownResponse>(tutorUrl),
      counselors: this.http.get<DropdownResponse>(counselorUrl),
      group: this.http.get<GroupDropDown>(groupUrl),
      parents: this.http.get<DropdownResponse>(parentBaseUrl),
      admin: this.http.get<DropdownResponse>(adminUrl)
    });
  }
}
